'use client'

import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useEffect } from 'react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { useLingui } from '@lingui/react/macro'
import { parseAsBoolean, useQueryState } from 'nuqs'

import { ResizablePanel } from '~/components/molecules/resizable-panel'
import { StripeProvider } from '~/providers/stripe-provider'
import { useBookingVisitStore } from './space-visit.store'
import { StepperIndicator } from './stepper-indicator'
import { DayStep } from './steps/day-step'
import { HourStep } from './steps/hour-step'
import { IntroStep } from './steps/intro-step'
import { PaymentStep } from './steps/payment-step'

export const useVisitDialog = () => {
	return useQueryState('visitDialog', parseAsBoolean.withDefault(false))
}

export const SpaceVisitStepper = ({ space }: { space: Space }) => {
	const { t } = useLingui()

	const init = useBookingVisitStore.use.init()
	const step = useBookingVisitStore.use.step()

	// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
	useEffect(() => init(), [])

	return (
		<div className='flex flex-col gap-4'>
			<DialogHeader>
				<DialogTitle>
					{step === 'intro' && t`Organiser une visite`}
					{step === 'day' && t`Choisissez une date`}
					{step === 'startTime' && t`Choisissez un créneau`}
					{step === 'payment' && t`Récapitulatif de votre visite`}
				</DialogTitle>
			</DialogHeader>

			<div className='flex flex-col gap-4'>
				<StepperIndicator />

				{step === 'intro' && <IntroStep />}
				{step === 'day' && <DayStep />}
				{step === 'startTime' && <HourStep spaceId={space.id} />}
				{step === 'payment' && <PaymentStep space={space} />}
			</div>
		</div>
	)
}

export const SpaceVisitDialog = ({
	space,
	children,
}: {
	space: Space
	children: React.ReactNode
}) => {
	const [isDialogOpen, setIsDialogOpen] = useVisitDialog()

	return (
		<Dialog
			open={isDialogOpen}
			onOpenChange={setIsDialogOpen}
		>
			<DialogTrigger asChild>{children}</DialogTrigger>

			<DialogContent className='flex !w-auto !max-w-0 min-w-[380px] flex-col gap-4 overflow-hidden pb-8'>
				<StripeProvider>
					<ResizablePanel>
						<SpaceVisitStepper space={space} />
					</ResizablePanel>
				</StripeProvider>
			</DialogContent>
		</Dialog>
	)
}
